import {AsyncPipe, DecimalPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {TranslatePipe} from '@ngx-translate/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {Layer, marker} from 'leaflet';
import {map} from 'rxjs';
import {Store} from '@ngrx/store';
import {ApiCisternResponseDto, DeviceTargetType, DeviceType, PowerSource} from '@birco/rainmesh-api';
import {InputWrapperComponent, isSubmitAllowed} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {MapComponent} from '../../../map/components/map/map.component';
import {MapConstants} from '../../../map/constants/map-constants';
import {CisternActions} from '../../../cisterns/state/cistern.actions';
import {selectAllCisterns} from '../../../cisterns/state/cistern.selectors';
import {LabelConverter} from '../../../../shared/converter/label.converter';
import {selectAllDevices} from '../../state/device.selectors';

type DeviceDialogData = {
  device?: {
    id?: string,
    name?: string,
    type?: DeviceType,
    validTypes?: Array<DeviceType>;
    powerSource?: PowerSource | null,
    batteryMinVoltage?: number | null,
    batteryMaxVoltage?: number | null,
    sourcePumpId?: string | null,
    sourcePumpThroughput?: number | null,
    deviceTargetType?: DeviceTargetType | null,
    targetCisternId?: string | null,
    location?: {
      lat: number,
      lon: number,
    } | null,
  }
}

type DeviceLocation = {
  lat: number,
  lon: number,
} | null;

@Component({
  imports: [AsyncPipe, DecimalPipe, MatDialogModule, TranslatePipe, ReactiveFormsModule, InputWrapperComponent, IconComponent, MapComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dialog-update-device.component.html',
  styleUrl: './dialog-update-device.component.scss',
})
export class DialogUpdateDeviceComponent {
  private static readonly defaultBatteryMinVoltage = 2.0;
  private static readonly defaultBatteryMaxVoltage = 3.6;
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  data = inject<DeviceDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DialogUpdateDeviceComponent>);
  private readonly store = inject(Store);
  form: FormGroup;
  isMapVisible = false;
  hasLocationChanged = false;
  isCisternTarget = false;
  locationLayers: Layer[] = [];
  readonly initialLocation = this.data?.device?.location ?? null;
  hasBatteryPower = this.data?.device?.powerSource === PowerSource.BATTERY;
  readonly powerSourceOptions: { label: string, value: PowerSource }[] = [
    {label: 'devices.edit.powerSource.options.battery', value: PowerSource.BATTERY},
    {label: 'devices.edit.powerSource.options.acPower', value: PowerSource.AC_POWER},
  ];
  readonly cisterns$ = this.store.select(selectAllCisterns).pipe(
    map((cisterns: ApiCisternResponseDto[]) => cisterns
      .slice()
      .sort((left, right) => left.name.localeCompare(right.name)))
  );
  readonly pumps$ = this.store.select(selectAllDevices).pipe(
    map((devices) => devices.filter(d => d.type === DeviceType.PUMP))
  );
  readonly isValve = this.data?.device?.type === DeviceType.VALVE;
  readonly targetTypeOptions: { label: string, value: DeviceTargetType }[] = [
    {label: 'devices.edit.targetTypeOptions.none', value: DeviceTargetType.NONE},
    {label: 'devices.edit.targetTypeOptions.consumer', value: DeviceTargetType.CONSUMER},
    {label: 'devices.edit.targetTypeOptions.cistern', value: DeviceTargetType.CISTERN},
  ];

readonly validTypeOptions: { label: string; value: DeviceType }[] = [
  ...(this.data.device?.validTypes ?? [this.data.device?.type]),
].filter(t => !!t).map((t) => ({
  label: LabelConverter.getDeviceTypeLabel(t),
  value: t,
}));

  constructor() {
    this.form = this.fb.group({
      name: [this.data?.device?.name, Validators.required],
      powerSource: [this.data?.device?.powerSource ?? null],
      location: [this.data?.device?.location ?? null],
      type: [this.data?.device?.type, Validators.required],
      batteryMinVoltage: [this.normalizeBatteryVoltage(this.data?.device?.batteryMinVoltage, DialogUpdateDeviceComponent.defaultBatteryMinVoltage), Validators.min(0)],
      batteryMaxVoltage: [this.normalizeBatteryVoltage(this.data?.device?.batteryMaxVoltage, DialogUpdateDeviceComponent.defaultBatteryMaxVoltage), Validators.min(0)],
      sourcePumpId: [this.data?.device?.sourcePumpId ?? null],
      deviceTargetType: [this.data?.device?.deviceTargetType ?? DeviceTargetType.NONE],
      targetCisternId: [this.data?.device?.targetCisternId ?? null],
      sourcePumpThroughput: [this.data?.device?.sourcePumpThroughput ?? null],
    });

    if (this.isValve) {
      this.store.dispatch(CisternActions.public.loadAllCisternsByFilters({}));
    }

    this.syncPowerSourceState(this.form.controls['powerSource'].value);

    if (!this.isValve) {
      this.form.controls['deviceTargetType'].disable({emitEvent: false});
      this.form.controls['targetCisternId'].disable({emitEvent: false});
    }

    this.syncLocationState(this.form.controls['location'].value);
    this.syncTargetCisternState(this.form.controls['deviceTargetType'].value);

    this.form.controls['location'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((location: DeviceLocation) => this.syncLocationState(location));

    this.syncBatteryVoltageValidation();

    this.form.controls['batteryMinVoltage'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncBatteryVoltageValidation());

    this.form.controls['batteryMaxVoltage'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncBatteryVoltageValidation());

    this.form.controls['powerSource'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((powerSource: PowerSource | null) => this.syncPowerSourceState(powerSource));

    this.form.controls['deviceTargetType'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deviceTargetType: DeviceTargetType | null) => this.syncTargetCisternState(deviceTargetType));
  }

  private syncPowerSourceState(powerSource: PowerSource | null) {
    this.hasBatteryPower = powerSource === PowerSource.BATTERY;

    if (this.hasBatteryPower) {
      this.form.controls['batteryMinVoltage'].enable({emitEvent: false});
      this.form.controls['batteryMaxVoltage'].enable({emitEvent: false});
    } else {
      this.form.controls['batteryMinVoltage'].disable({emitEvent: false});
      this.form.controls['batteryMaxVoltage'].disable({emitEvent: false});
    }

    this.syncBatteryVoltageValidation();
  }

  private syncBatteryVoltageValidation() {
    const batteryMinVoltage = this.form.controls['batteryMinVoltage'].value;
    const batteryMaxVoltage = this.form.controls['batteryMaxVoltage'].value;
    const errors = {...this.form.controls['batteryMaxVoltage'].errors} as ValidationErrors;

    delete errors['greaterThanMin'];

    if (
      batteryMinVoltage !== null
      && batteryMinVoltage !== undefined
      && batteryMaxVoltage !== null
      && batteryMaxVoltage !== undefined
      && batteryMaxVoltage <= batteryMinVoltage
    ) {
      errors['greaterThanMin'] = true;
    }

    this.form.controls['batteryMaxVoltage'].setErrors(Object.keys(errors).length ? errors : null);
  }

  private syncTargetCisternState(deviceTargetType: DeviceTargetType | null) {
    if (!this.isValve) {
      this.isCisternTarget = false;
      this.form.controls['targetCisternId'].removeValidators(Validators.required);
      this.form.controls['targetCisternId'].disable({emitEvent: false});
      this.form.controls['targetCisternId'].updateValueAndValidity({emitEvent: false});
      return;
    }

    this.isCisternTarget = deviceTargetType === DeviceTargetType.CISTERN;

    if (deviceTargetType === DeviceTargetType.CISTERN) {
      this.form.controls['targetCisternId'].addValidators(Validators.required);
      this.form.controls['targetCisternId'].enable({emitEvent: false});
    } else {
      this.form.controls['targetCisternId'].removeValidators(Validators.required);
      this.form.controls['targetCisternId'].setValue(null, {emitEvent: false});
      this.form.controls['targetCisternId'].disable({emitEvent: false});
    }

    this.form.controls['targetCisternId'].updateValueAndValidity({emitEvent: false});
  }

  private syncLocationState(location: DeviceLocation) {
    this.hasLocationChanged = !this.locationsEqual(location, this.initialLocation);
    this.locationLayers = this.createLocationLayers(location);
  }

  onMapClick(location: { lat: number; lon: number }) {
    this.updateSelectedLocation(location);
  }

  toggleMapVisibility(showMap?: boolean) {
    this.isMapVisible = showMap ?? !this.isMapVisible;
  }

  resetLocation() {
    this.updateSelectedLocation(this.initialLocation);
  }

  private updateSelectedLocation(location: DeviceLocation) {
    this.form.patchValue({ location });
  }

  private createLocationLayers(location: DeviceLocation): Layer[] {
    if (!location) return [];

    const locationMarker = marker([location.lat, location.lon], {
      icon: MapConstants.DefaultMapIcon,
      draggable: true,
    });

    locationMarker.on('dragend', () => {
      const {lat, lng} = locationMarker.getLatLng();
      this.updateSelectedLocation({lat, lon: lng});
    });

    return [locationMarker];
  }

  private locationsEqual(
    first: DeviceLocation,
    second: DeviceLocation
  ) {
    if (!first && !second) return true;
    if (!first || !second) return false;

    return first.lat === second.lat && first.lon === second.lon;
  }

  onSubmit() {
    if (!isSubmitAllowed(this.form)) {
      return;
    }
      const batteryVoltagePayload = this.hasBatteryPower
        ? {
          batteryMinVoltage: this.normalizeBatteryVoltage(
            this.form.controls['batteryMinVoltage'].value,
            DialogUpdateDeviceComponent.defaultBatteryMinVoltage,
          ),
          batteryMaxVoltage: this.normalizeBatteryVoltage(
            this.form.controls['batteryMaxVoltage'].value,
            DialogUpdateDeviceComponent.defaultBatteryMaxVoltage,
          ),
        }
        : {};

      // `type` is renamed to `deviceType` because NgRx's action discriminator
      // uses the `type` key — the effect maps `deviceType` back to `type` before
      // the API call. Emitting raw `type` here would get clobbered at dispatch.
      const { type, ...formValue } = this.form.value;
      this.dialogRef.close({
        id: this.data.device?.id,
        ...formValue,
        deviceType: type,
        ...batteryVoltagePayload,
      });
  }

  private normalizeBatteryVoltage(value: number | null | undefined, fallback: number): number {
    return value === null || value === undefined ? fallback : value;
  }
}
